<template>
  <el-dialog
    title="阈值配置"
    :model-value="modelValue"
    width="760px"
    :close-on-click-modal="false"
    @update:model-value="onVisibleChange"
    @closed="onClosed"
  >
    <div class="threshold-dialog">
      <el-form :inline="true" size="mini">
        <el-form-item label="分类">
          <el-select
            v-model="name"
            filterable
            placeholder="请选择分类"
            style="width: 260px"
            @change="onNameChange"
          >
            <el-option
              v-for="item in categoryList"
              :key="item.name"
              :label="item.name"
              :value="item.name"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="mini" @click="loadConfig"
            >加载当前配置</el-button
          >
        </el-form-item>
        <el-form-item v-if="!isEmpty(currentSource)">
          <span class="source-tag" :class="currentSource">
            来源：{{ sourceText }}
          </span>
        </el-form-item>
      </el-form>

      <el-form label-position="top" size="mini">
        <el-form-item label="阈值 JSON">
          <el-input
            v-model="thresholdsText"
            type="textarea"
            :rows="16"
            placeholder='{"无双+": {"MI 8": {"avg_fps": "> 50.00"}}}'
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="remark"
            placeholder="建议填写本次修改原因"
          />
        </el-form-item>
      </el-form>

      <div class="dialog-footer">
        <el-button
          size="mini"
          :disabled="loadingHistory"
          @click="openHistory"
          >查看历史</el-button
        >
        <span class="flex-spacer"></span>
        <el-button size="mini" @click="close">取消</el-button>
        <el-button
          type="primary"
          size="mini"
          :loading="saving"
          @click="saveConfig"
          >保存</el-button
        >
      </div>
    </div>

    <el-dialog
      title="历史版本"
      v-model="historyVisible"
      width="560px"
      append-to-body
    >
      <el-table :data="historyList" size="mini" max-height="360">
        <el-table-column label="更新时间" width="180">
          <template #default="{ row }">{{
            formatTime(row.update_time)
          }}</template>
        </el-table-column>
        <el-table-column prop="remark" label="备注"></el-table-column>
        <el-table-column label="操作" width="180">
          <template #default="{ row, $index }">
            <el-button
              type="text"
              size="mini"
              @click="restoreHistory(row)"
              >回退到此版本</el-button
            >
            <el-button
              v-if="$index === 0"
              type="text"
              size="mini"
              style="color: #f56c6c"
              @click="deleteLatest"
              >删除</el-button
            >
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!historyList.length" class="history-empty">暂无历史版本</div>
      <div v-if="historyList.length" class="history-footer">
        <el-button
          type="danger"
          size="mini"
          plain
          @click="deleteAllHistory"
          >删除全部历史</el-button
        >
      </div>
    </el-dialog>
  </el-dialog>
</template>

<script>
import axios from 'axios'
import { ElMessage } from 'element-plus'

export default {
  name: 'ThresholdConfigDialog',

  emits: ['update:modelValue'],

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    project: {
      type: [String, Number],
      default: 57,
    },
  },

  data() {
    return {
      name: '',
      categoryList: [],
      currentSource: '',
      thresholdsText: '',
      remark: '',
      saving: false,
      loadingHistory: false,
      historyVisible: false,
      historyList: [],
    }
  },

  computed: {
    sourceText() {
      const map = {
        project: '项目级配置',
        global: '全局配置',
        default: '代码内置默认',
      }
      return map[this.currentSource] || ''
    },
  },

  watch: {
    modelValue(val) {
      if (val && !this.categoryList.length) {
        this.init()
      }
    },
  },

  methods: {
    isEmpty(val) {
      return val === null || val === undefined || val === ''
    },

    formatTime(val) {
      if (!val) return '--'
      const s = String(val).replace('T', ' ')
      // 去掉秒后的小数部分，例如 "2026-10-10 10:52:16.22" -> "2026-10-10 10:52:16"
      return s.replace(/\.\d+$/, '')
    },

    async init() {
      await this.fetchCategoryList()
    },

    async fetchCategoryList() {
      try {
        const { data } = await axios.get('/report/threshold_config_list/', {
          params: { project: this.project },
        })
        this.categoryList = data.results || []
        if (this.categoryList.length && !this.name) {
          this.name = this.categoryList[0].name
          this.onNameChange()
        }
      } catch (e) {
        console.error('获取阈值分类失败', e)
        ElMessage.error('获取阈值分类失败')
      }
    },

    onNameChange() {
      const item = this.categoryList.find((v) => v.name === this.name)
      if (item) {
        this.fillFromResult(item)
      }
    },

    fillFromResult(item) {
      this.currentSource = item.source || ''
      this.thresholdsText = JSON.stringify(item.thresholds, null, 2)
      this.remark = item.remark || ''
    },

    async loadConfig() {
      if (!this.name) {
        ElMessage.warning('请先选择分类')
        return
      }
      try {
        const { data } = await axios.get('/report/threshold_config_list/', {
          params: { project: this.project, name: this.name },
        })
        const results = data.results || []
        const item = results.find((v) => v.name === this.name)
        if (!item) {
          ElMessage.warning('未找到该分类配置')
          return
        }
        this.fillFromResult(item)
        ElMessage.success('已加载最新配置')
      } catch (e) {
        console.error('加载配置失败', e)
        ElMessage.error('加载配置失败')
      }
    },

    async saveConfig() {
      if (!this.name) {
        ElMessage.warning('请先选择分类')
        return
      }

      let thresholds
      try {
        thresholds = JSON.parse(this.thresholdsText)
      } catch (e) {
        ElMessage.error('thresholds 不是合法的 JSON')
        return
      }
      if (!thresholds || typeof thresholds !== 'object' || Array.isArray(thresholds)) {
        ElMessage.error('thresholds 必须是 JSON 对象')
        return
      }

      this.saving = true
      try {
        await axios.post('/report/threshold_config_save/', {
          project: this.project,
          name: this.name,
          thresholds,
          remark: this.remark || '',
        })
        ElMessage.success('保存成功，10 秒内生效')
        this.close()
      } catch (e) {
        console.error('保存配置失败', e)
        ElMessage.error(e?.response?.data || '保存失败')
      } finally {
        this.saving = false
      }
    },

    async openHistory() {
      if (!this.name) {
        ElMessage.warning('请先选择分类')
        return
      }
      this.loadingHistory = true
      this.historyList = []
      try {
        const { data } = await axios.get('/report/threshold_config_list/', {
          params: { project: this.project, name: this.name, history: 1 },
        })
        this.historyList = data.history || []
        this.historyVisible = true
      } catch (e) {
        console.error('获取历史版本失败', e)
        ElMessage.error('获取历史版本失败')
      } finally {
        this.loadingHistory = false
      }
    },

    async deleteLatest() {
      try {
        await this.$confirm(
          '确认删除最新一条配置？删除后将回退到上一版本。',
          '删除确认',
          { type: 'warning' },
        )
      } catch (e) {
        return
      }
      try {
        await axios.post('/report/threshold_config_delete/', {
          project: this.project,
          name: this.name,
        })
        ElMessage.success('删除成功')
        this.historyVisible = false
        await this.loadConfig()
      } catch (e) {
        console.error('删除失败', e)
        ElMessage.error(e?.response?.data || '删除失败')
      }
    },

    async deleteAllHistory() {
      try {
        await this.$confirm(
          '确认删除全部历史？删除后将回退到全局/内置默认配置。',
          '删除确认',
          { type: 'warning' },
        )
      } catch (e) {
        return
      }
      try {
        await axios.post('/report/threshold_config_delete/', {
          project: this.project,
          name: this.name,
          all: true,
        })
        ElMessage.success('删除成功')
        this.historyVisible = false
        await this.loadConfig()
      } catch (e) {
        console.error('删除失败', e)
        ElMessage.error(e?.response?.data || '删除失败')
      }
    },

    async restoreHistory(row) {
      try {
        await this.$confirm(
          `确认回退到该版本（${this.formatTime(row.update_time)}）？将新增一条配置。`,
          '回退确认',
          { type: 'warning' },
        )
      } catch (e) {
        return
      }
      try {
        await axios.post('/report/threshold_config_save/', {
          project: this.project,
          name: this.name,
          restore_id: row.id,
        })
        ElMessage.success('回退成功')
        this.historyVisible = false
        await this.loadConfig()
      } catch (e) {
        console.error('回退失败', e)
        ElMessage.error(e?.response?.data || '回退失败')
      }
    },

    onVisibleChange(val) {
      this.$emit('update:modelValue', val)
    },

    close() {
      this.$emit('update:modelValue', false)
    },

    onClosed() {
      this.name = ''
      this.categoryList = []
      this.currentSource = ''
      this.thresholdsText = ''
      this.remark = ''
      this.historyVisible = false
      this.historyList = []
    },
  },
}
</script>

<style scoped>
.threshold-dialog :deep(.el-form-item__label) {
  color: #333;
}

.source-tag {
  display: inline-block;
  padding: 2px 8px;
  font-size: 12px;
  border-radius: 3px;
}
.source-tag.project {
  color: #67c23a;
  background: #f0f9eb;
}
.source-tag.global {
  color: #e6a23c;
  background: #fdf6ec;
}
.source-tag.default {
  color: #909399;
  background: #f4f4f5;
}

.dialog-footer {
  display: flex;
  align-items: center;
}
.flex-spacer {
  flex: 1;
}

.history-empty {
  text-align: center;
  color: #909399;
  padding: 20px 0;
}

.history-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
</style>
